import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";

import { App, Workspace } from "@common/models";
import { Plan, PlanProductName } from "@common/models/billing";
import { Member } from "@common/models/Member.model";
import { ordinal } from "@common/util/strings";

import { useAppContext } from "../App/App.context";
import { useAppSelectionContext } from "../App/AppSelectionContext";

export enum SubscriptionState {
  good = "good",
  noApps = "no_apps",
  usageExceedsCurrentSubscription = "usage_exceeds",
}

type SubContext = {
  state: SubscriptionState | undefined;
};
const SubscriptionContext = createContext<SubContext>({} as SubContext);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const value = useSubscriptionState();
  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscriptionContext = () => useContext(SubscriptionContext);

const useSubscriptionState = () => {
  const { apps, loading: appsLoading, updatingSelectedWorkspace } = useAppContext();
  const { selectedWorkspace } = useAppSelectionContext();
  const [state, setState] = useState<SubscriptionState>();

  useEffect(() => {
    if (appsLoading || !selectedWorkspace || updatingSelectedWorkspace) return setState(undefined);

    if (!selectedWorkspace.is_data_available) return setState(SubscriptionState.usageExceedsCurrentSubscription);

    if (apps.length === 0 || !apps.find((a) => a.active)) return setState(SubscriptionState.noApps);

    return setState(SubscriptionState.good);
  }, [appsLoading, apps, selectedWorkspace, updatingSelectedWorkspace]);

  return { state };
};

// Subscription team members limit

export const FREE_PLAN_MEMBERS_LIMIT = 3;
export const PREMIUM_PLAN_MEMBERS_LIMIT = 25;
export const ORGANIZATION_PLAN_MEMBERS_LIMIT = 100;

export const isTeamMembersLimitReached = (members?: Member[], plan?: Plan): boolean => {
  const teamMembersNum: number = members?.length ?? 0;
  const teamMembersPlanLimit = getTeamMembersPlanLimit(plan);

  return teamMembersNum > teamMembersPlanLimit;
};

export const getTeamMembersPlanLimit = (plan?: Plan): number => {
  if (!plan || !plan.product_name) return FREE_PLAN_MEMBERS_LIMIT;
  if (plan.product_name === PlanProductName.PREMIUM) return PREMIUM_PLAN_MEMBERS_LIMIT;
  if (plan.product_name === PlanProductName.ORGANIZATION) return ORGANIZATION_PLAN_MEMBERS_LIMIT;
  return FREE_PLAN_MEMBERS_LIMIT;
};

// Subscription apps limit

export const FREE_PLAN_APPS_LIMIT = 1;
export const PREMIUM_PLAN_APPS_LIMIT = 5;
export const ORGANIZATION_PLAN_APPS_LIMIT = 20;

export const isAppsNumberLimitReached = (activeApps?: App[], plan?: Plan): boolean => {
  const appsNum: number = activeApps?.filter((a) => !a.is_extension)?.length ?? 0;
  const appsPlanLimit = getAppsPlanLimit(plan);

  return appsNum > appsPlanLimit;
};

export const getAppsPlanLimit = (plan?: Plan): number => {
  if (!plan || !plan.product_name) return FREE_PLAN_APPS_LIMIT;
  if (plan.product_name === PlanProductName.PREMIUM) return PREMIUM_PLAN_APPS_LIMIT;
  if (plan.product_name === PlanProductName.ORGANIZATION) return ORGANIZATION_PLAN_APPS_LIMIT;
  return FREE_PLAN_APPS_LIMIT;
};

// Add-on sdk initializations limit

export const FREE_PLAN_SDK_INITS_LIMIT = 10000;
export const FREE_PLAN_SDK_INITS_LIMIT_STRING = "10k";

export const isSdkInitializationsLimitReached = (workspace?: Workspace, addOn?: Plan): boolean => {
  const uniqueDevicesNum: number = workspace?.unique_devices ?? 0;
  const addOnSdkInitializationsLimit = getAddOnSdkInitializationsLimit(addOn);

  return uniqueDevicesNum > addOnSdkInitializationsLimit;
};

export const getAddOnSdkInitializationsLimit = (addOn?: Plan): number => {
  let addOnNum = FREE_PLAN_SDK_INITS_LIMIT;

  if (!addOn || !addOn.product_name) return addOnNum;

  const addOnNumString = addOn.product_name.split(" ")?.[0]?.toLowerCase();
  if (addOnNumString.includes("k")) addOnNum = parseInt(addOnNumString.replace("k", "")) * 1000; // Premium
  if (addOnNumString.includes("m")) addOnNum = parseInt(addOnNumString.replace("m", "")) * 1000000; // Organization

  return addOnNum;
};

export const getAddOnSdkInitializationsLimitString = (addOn?: Plan): string => {
  if (!addOn || !addOn.product_name) return FREE_PLAN_SDK_INITS_LIMIT_STRING;
  return addOn.product_name.split(" ")?.[0];
};

// Integration layout customization limit

export const isIntegrationLayoutCustomizationEnabled = (workspace?: Workspace): boolean => {
  if (workspace?.is_free) return true;
  const currentPlan = workspace?.subscription_plans?.find((item) => item.type === "plan");
  if (!currentPlan || !currentPlan.product_name) return false;
  return currentPlan.product_name === PlanProductName.ORGANIZATION;
};

// Workspace limit

export const didWSReachLimit = (workspace: Workspace, activeApps: App[], members: Member[]) => {
  const currentPlan: Plan | undefined = workspace.subscription_plans.find((item) => item.type === "plan");
  const currentAddOn: Plan | undefined = workspace.subscription_plans.find((item) => item.type === "devices");

  return (
    isTeamMembersLimitReached(members, currentPlan) ||
    isAppsNumberLimitReached(activeApps, currentPlan) ||
    isSdkInitializationsLimitReached(workspace, currentAddOn)
  );
};

export interface LimitNumbers {
  isBelowLimit: boolean;
  currentNum: number;
  nextNum: string;
  limit: number;
}

export const getAppsLimitNumbers = (activeApps: App[], subscriptionPlans: Plan[]): LimitNumbers | null => {
  const currentPlan: Plan | undefined = subscriptionPlans.find((item) => item.type === "plan");
  const activeAppsCount: number = activeApps.filter((a) => !a.is_extension)?.length ?? 0;
  const appsPlanLimit: number = getAppsPlanLimit(currentPlan);

  const isAtLimit = activeAppsCount === appsPlanLimit;
  const isOneBelowLimit = activeAppsCount > 0 && activeAppsCount === appsPlanLimit - 1;
  if (!isAtLimit && !isOneBelowLimit) return null;

  return {
    isBelowLimit: isOneBelowLimit,
    currentNum: activeAppsCount,
    nextNum: ordinal(activeAppsCount + 1),
    limit: appsPlanLimit,
  };
};
